import { AxiosInstance } from "axios";
import { Method, Resource } from "../Resource";

export type DocumentRequest = {
  clientId?: string | null;
  type?: string | null;
  classification?: string | null;
  issuingCountry?: string | null;
};

export type Document = {
  id: string;
  clientId: string;
  type: string;
  classification?: string | null;
  issuingCountry?: string | null;
  lastActionBy?: string | null;
};

export type ImageUploadRequest = {
  fileName: string;
  data: string;
};

export type RedactionRequest = {
  attributes: string[] | null;
  images?: string[] | null;
};

export type Image = {
  id: string;
  fileName: string;
  documentSide: string;
  downloadLink: string;
  contentType: string;
  size: number;
  createdAt: string;
  updatedAt: string;
};

export class Documents extends Resource<{}> {
  constructor(axiosInstance: AxiosInstance) {
    super("documents", axiosInstance);
  }

  public async create(
    clientId: string,
    documentRequest: DocumentRequest
  ): Promise<Document> {
    return this.request({
      method: Method.POST,
      body: { clientId, ...documentRequest }
    });
  }

  public async get(id: string): Promise<Document> {
    return this.request({ method: Method.GET, path: id });
  }

  public async update(
    id: string,
    documentRequest: DocumentRequest
  ): Promise<Document> {
    return this.request({
      method: Method.POST,
      path: id,
      body: documentRequest
    });
  }

  public async upload(
    id: string,
    imageUploadRequest: ImageUploadRequest,
    documentSide: string
  ): Promise<Image> {
    return super.upload(id, imageUploadRequest, documentSide);
  }

  public async download(id: string, documentSide: string): Promise<Image> {
    return super.download(id, documentSide);
  }

  public async deleteImage(id: string, documentSide: string): Promise<void> {
    const path = `${id}/${documentSide}`;
    await this.request({ method: Method.DELETE, path });
  }

  public async redact(
    id: string,
    redactionRequest: RedactionRequest
  ): Promise<void> {
    return this.request({
      method: Method.POST,
      path: `${id}/redact`,
      body: redactionRequest
    });
  }

  public async delete(id: string): Promise<void> {
    await this.request({ method: Method.DELETE, path: id });
  }

  public async list(
    clientId: string,
    {
      page,
      pageSize
    }: {
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<Document[]> {
    const { items: documents } = await this.request({
      method: Method.GET,
      query: { clientId, page, pageSize }
    });

    return documents;
  }
}
